import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  Button, 
  Table, 
  Badge, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Alert,
  Spinner
} from 'reactstrap';
import { teamApi } from '../../helpers/team_api_helper';
import BreadCrumb from '../../Components/Common/BreadCrumb';

interface User {
  userId: number;
  fullName: string;
  email: string;
  userRole: number;
  status: number;
  createdAt: string;
  updatedAt: string;
  lastLoginDate: string | null;
}

interface ApiResponse<T> {
  status: number;
  message: string;
  data: T;
}

const TeamManagement: React.FC = () => {
  const [teamUsers, setTeamUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);
  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState<boolean>(false);
  const [showReactivateModal, setShowReactivateModal] = useState<boolean>(false);
  const [addUserLoading, setAddUserLoading] = useState<boolean>(false);
  const [editUserLoading, setEditUserLoading] = useState<boolean>(false);
  const [deactivateLoading, setDeactivateLoading] = useState<boolean>(false);
  const [reactivateLoading, setReactivateLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const [userToReactivate, setUserToReactivate] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: ''
  });
  const [editFormData, setEditFormData] = useState({
    fullName: '',
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [editFormErrors, setEditFormErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    loadTeamUsers();
  }, []);

  const loadTeamUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await teamApi.getTeamUsers();
      console.log('Team users response:', response);
      
      if (response.status === 200) {
        setTeamUsers(response.data || []);
      } else {
        setError(response.data?.message || 'Failed to load team users');
      }
    } catch (err: any) {
      console.error('Error loading team users:', err);
      setError(err.response?.data?.message || 'Failed to load team users');
    } finally {
      setLoading(false);
    }
  };

  const getUserRoleBadge = (role: number) => {
    switch (role) {
      case 8:
        return <Badge color="primary">Admin</Badge>;
      case 1:
        return <Badge color="secondary">Developer</Badge>;
      default:
        return <Badge color="light">Unknown</Badge>;
    }
  };

  const getUserStatusBadge = (status: number) => {
    switch (status) {
      case 1:
        return <Badge color="success">Active</Badge>;
      case 2:
        return <Badge color="danger">Cancelled</Badge>;
      case 3:
        return <Badge color="warning">Suspended</Badge>;
      default:
        return <Badge color="light">Unknown</Badge>;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setAddUserLoading(true);
      const response = await teamApi.addTeamUser(formData);
      console.log('Add user response:', response);
      
      if (response.status === 201) {
        // Success - refresh the team users list
        await loadTeamUsers();
        setShowAddUserModal(false);
        setFormData({ fullName: '', email: '', password: '' });
        setFormErrors({});
        setError(null);
      } else {
        setError(response.data?.message || 'Failed to add team user');
      }
    } catch (err: any) {
      console.error('Error adding team user:', err);
      setError(err.response?.data?.message || 'Failed to add team user');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowAddUserModal(false);
    setFormData({ fullName: '', email: '', password: '' });
    setFormErrors({});
    setError(null);
  };

  const handleEditModalClose = () => {
    setShowEditUserModal(false);
    setEditFormData({ fullName: '', email: '', password: '' });
    setEditFormErrors({});
    setSelectedUser(null);
    setError(null);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      fullName: user.fullName,
      email: user.email,
      password: '' // Don't populate password for security
    });
    setEditFormErrors({});
    setError(null);
    setShowEditUserModal(true);
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field error when user starts typing
    if (editFormErrors[name]) {
      setEditFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateEditForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!editFormData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!editFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Password is optional for edit - only validate if provided
    if (editFormData.password && editFormData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }
    
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEditForm() || !selectedUser) {
      return;
    }
    
    try {
      setEditUserLoading(true);
      const response = await teamApi.updateTeamUser(selectedUser.userId, editFormData);
      console.log('Update user response:', response);
      
      if (response.status === 200) {
        // Success - refresh the team users list
        await loadTeamUsers();
        setShowEditUserModal(false);
        setEditFormData({ fullName: '', email: '', password: '' });
        setEditFormErrors({});
        setSelectedUser(null);
        setError(null);
      } else {
        setError(response.data?.message || 'Failed to update team user');
      }
    } catch (err: any) {
      console.error('Error updating team user:', err);
      setError(err.response?.data?.message || 'Failed to update team user');
    } finally {
      setEditUserLoading(false);
    }
  };

  const handleDeactivateUser = (user: User) => {
    setUserToDeactivate(user);
    setError(null);
    setShowDeactivateModal(true);
  };

  const handleDeactivateModalClose = () => {
    setShowDeactivateModal(false);
    setUserToDeactivate(null);
    setError(null); // Clear error when manually closing modal
  };

  const confirmDeactivateUser = async () => {
    if (!userToDeactivate) return;
    
    try {
      setDeactivateLoading(true);
      // Status 2 = USER_STATUS_CANCELLED (deactivated)
      const response = await teamApi.updateTeamUserStatus(userToDeactivate.userId, 2);
      console.log('Deactivate user response:', response);
      
      if (response.status === 200) {
        // Success - refresh the team users list
        await loadTeamUsers();
        setShowDeactivateModal(false);
        setUserToDeactivate(null);
        setError(null);
      } else {
        // Failure - keep modal open and show error
        setError(response.data?.message || 'Failed to deactivate team user');
      }
    } catch (err: any) {
      console.error('Error deactivating team user:', err);
      setError(err.response?.data?.message || 'Failed to deactivate team user');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleReactivateUser = (user: User) => {
    setUserToReactivate(user);
    setError(null);
    setShowReactivateModal(true);
  };

  const handleReactivateModalClose = () => {
    setShowReactivateModal(false);
    setUserToReactivate(null);
    setError(null); // Clear error when manually closing modal
  };

  const confirmReactivateUser = async () => {
    if (!userToReactivate) return;
    
    try {
      setReactivateLoading(true);
      // Status 1 = USER_STATUS_ACTIVE
      const response = await teamApi.updateTeamUserStatus(userToReactivate.userId, 1);
      console.log('Reactivate user response:', response);
      
      if (response.status === 200) {
        // Success - refresh the team users list
        await loadTeamUsers();
        setShowReactivateModal(false);
        setUserToReactivate(null);
        setError(null);
      } else {
        // Failure - keep modal open and show error
        setError(response.data?.message || 'Failed to reactivate team user');
      }
    } catch (err: any) {
      console.error('Error reactivating team user:', err);
      setError(err.response?.data?.message || 'Failed to reactivate team user');
    } finally {
      setReactivateLoading(false);
    }
  };

  document.title = "Team Management | Velzon - React Admin & Dashboard Template";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Team Management" pageTitle="Admin" />
          
          {error && (
            <Alert color="danger" className="mb-4">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </Alert>
          )}

          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h5 className="card-title mb-0">Team Members</h5>
                      <p className="text-muted mb-0">Manage your team members and their permissions</p>
                    </div>
                    <Button 
                      color="primary" 
                      onClick={() => setShowAddUserModal(true)}
                      className="d-flex align-items-center gap-2"
                    >
                      <i className="ri-add-line"></i>
                      Add Team Member
                    </Button>
                  </div>

                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" className="me-2" />
                      Loading team members...
                    </div>
                  ) : teamUsers.length > 0 ? (
                    <div className="table-responsive">
                      <Table className="table-nowrap align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Email</th>
                            <th scope="col">Role</th>
                            <th scope="col">Status</th>
                            <th scope="col">Joined</th>
                            <th scope="col">Last Login</th>
                            <th scope="col">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamUsers.map((user) => (
                            <tr key={user.userId}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="avatar-xs me-3">
                                    <div className="avatar-title rounded-circle bg-soft-primary text-primary">
                                      {user.fullName.charAt(0).toUpperCase()}
                                    </div>
                                  </div>
                                  <div>
                                    <h6 className="mb-0">{user.fullName}</h6>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="text-muted">{user.email}</span>
                              </td>
                              <td>
                                {getUserRoleBadge(user.userRole)}
                              </td>
                              <td>
                                {getUserStatusBadge(user.status)}
                              </td>
                              <td>
                                <span className="text-muted">
                                  {new Date(user.createdAt).toLocaleDateString()}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {user.lastLoginDate 
                                    ? new Date(user.lastLoginDate).toLocaleString()
                                    : 'Never'
                                  }
                                </span>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button 
                                    size="sm" 
                                    color="soft-primary"
                                    title="Edit User"
                                    onClick={() => handleEditUser(user)}
                                  >
                                    <i className="ri-edit-line"></i>
                                  </Button>
                                  {user.status === 1 ? (
                                    <Button 
                                      size="sm" 
                                      color="soft-danger"
                                      title="Deactivate User"
                                      onClick={() => handleDeactivateUser(user)}
                                    >
                                      <i className="ri-user-forbid-line"></i>
                                    </Button>
                                  ) : (
                                    <Button 
                                      size="sm" 
                                      color="soft-success"
                                      title="Reactivate User"
                                      onClick={() => handleReactivateUser(user)}
                                    >
                                      <i className="ri-user-add-line"></i>
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-light text-muted rounded-circle">
                          <i className="ri-team-line fs-24"></i>
                        </div>
                      </div>
                      <h5 className="mb-2">No Team Members</h5>
                      <p className="text-muted">Add team members to get started with collaboration</p>
                      <Button 
                        color="primary" 
                        onClick={() => setShowAddUserModal(true)}
                        className="d-flex align-items-center gap-2 mx-auto"
                      >
                        <i className="ri-add-line"></i>
                        Add First Team Member
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add User Modal */}
      <Modal isOpen={showAddUserModal} toggle={handleModalClose} size="md" centered>
        <ModalHeader toggle={handleModalClose}>
          Add New Team Member
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleAddUser}>
            <FormGroup>
              <Label for="fullName">Full Name <span className="text-danger">*</span></Label>
              <Input
                type="text"
                name="fullName"
                id="fullName"
                placeholder="Enter full name"
                value={formData.fullName}
                onChange={handleInputChange}
                invalid={!!formErrors.fullName}
              />
              {formErrors.fullName && (
                <div className="invalid-feedback">{formErrors.fullName}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="email">Email Address <span className="text-danger">*</span></Label>
              <Input
                type="email"
                name="email"
                id="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleInputChange}
                invalid={!!formErrors.email}
              />
              {formErrors.email && (
                <div className="invalid-feedback">{formErrors.email}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="password">Password <span className="text-danger">*</span></Label>
              <Input
                type="password"
                name="password"
                id="password"
                placeholder="Enter password (min. 6 characters)"
                value={formData.password}
                onChange={handleInputChange}
                invalid={!!formErrors.password}
              />
              {formErrors.password && (
                <div className="invalid-feedback">{formErrors.password}</div>
              )}
              <small className="text-muted">Password must be at least 6 characters long</small>
            </FormGroup>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                type="button" 
                color="secondary" 
                onClick={handleModalClose}
                disabled={addUserLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                color="primary"
                disabled={addUserLoading}
              >
                {addUserLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="ri-add-line me-2"></i>
                    Add Team Member
                  </>
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={showEditUserModal} toggle={handleEditModalClose} size="md" centered>
        <ModalHeader toggle={handleEditModalClose}>
          Edit Team Member
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleUpdateUser}>
            <FormGroup>
              <Label for="editFullName">Full Name <span className="text-danger">*</span></Label>
              <Input
                type="text"
                name="fullName"
                id="editFullName"
                placeholder="Enter full name"
                value={editFormData.fullName}
                onChange={handleEditInputChange}
                invalid={!!editFormErrors.fullName}
              />
              {editFormErrors.fullName && (
                <div className="invalid-feedback">{editFormErrors.fullName}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="editEmail">Email Address <span className="text-danger">*</span></Label>
              <Input
                type="email"
                name="email"
                id="editEmail"
                placeholder="Enter email address"
                value={editFormData.email}
                onChange={handleEditInputChange}
                invalid={!!editFormErrors.email}
                readOnly
                className="bg-light"
              />
              {editFormErrors.email && (
                <div className="invalid-feedback">{editFormErrors.email}</div>
              )}
              <small className="text-muted">Email cannot be changed</small>
            </FormGroup>

            <FormGroup>
              <Label for="editPassword">New Password</Label>
              <Input
                type="password"
                name="password"
                id="editPassword"
                placeholder="Enter new password (leave blank to keep current)"
                value={editFormData.password}
                onChange={handleEditInputChange}
                invalid={!!editFormErrors.password}
              />
              {editFormErrors.password && (
                <div className="invalid-feedback">{editFormErrors.password}</div>
              )}
              <small className="text-muted">Leave blank to keep current password. Minimum 6 characters if changing.</small>
            </FormGroup>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                type="button" 
                color="secondary" 
                onClick={handleEditModalClose}
                disabled={editUserLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                color="primary"
                disabled={editUserLoading}
              >
                {editUserLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-2"></i>
                    Update Team Member
                  </>
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Deactivate User Confirmation Modal */}
      <Modal isOpen={showDeactivateModal} toggle={handleDeactivateModalClose} size="md" centered>
        <ModalHeader toggle={handleDeactivateModalClose}>
          <i className="ri-error-warning-line text-warning me-2"></i>
          Confirm User Deactivation
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <div className="avatar-lg mx-auto mb-4">
              <div className="avatar-title bg-soft-warning text-warning rounded-circle">
                <i className="ri-user-forbid-line fs-24"></i>
              </div>
            </div>
            <h5 className="mb-3">Deactivate Team Member?</h5>
            {userToDeactivate && (
              <p className="text-muted mb-4">
                Are you sure you want to deactivate <strong>{userToDeactivate.fullName}</strong> 
                (<strong>{userToDeactivate.email}</strong>)? 
              </p>
            )}
            <p className="text-muted mb-0">
              The user will be marked as cancelled and will no longer have access to the system. 
              You can reactivate them later if needed.
            </p>
            
            {error && (
              <Alert color="danger" className="mt-3">
                <i className="ri-error-warning-line me-2"></i>
                {error}
              </Alert>
            )}
          </div>
          
          <div className="d-flex justify-content-center gap-2 mt-4">
            <Button 
              type="button" 
              color="secondary" 
              onClick={handleDeactivateModalClose}
              disabled={deactivateLoading}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              color="danger"
              onClick={confirmDeactivateUser}
              disabled={deactivateLoading}
            >
              {deactivateLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deactivating...
                </>
              ) : (
                <>
                  <i className="ri-user-forbid-line me-2"></i>
                  Deactivate User
                </>
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Reactivate User Confirmation Modal */}
      <Modal isOpen={showReactivateModal} toggle={handleReactivateModalClose} size="md" centered>
        <ModalHeader toggle={handleReactivateModalClose}>
          <i className="ri-checkbox-circle-line text-success me-2"></i>
          Confirm User Reactivation
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <div className="avatar-lg mx-auto mb-4">
              <div className="avatar-title bg-soft-success text-success rounded-circle">
                <i className="ri-user-add-line fs-24"></i>
              </div>
            </div>
            <h5 className="mb-3">Reactivate Team Member?</h5>
            {userToReactivate && (
              <p className="text-muted mb-4">
                Are you sure you want to reactivate <strong>{userToReactivate.fullName}</strong> 
                (<strong>{userToReactivate.email}</strong>)? 
              </p>
            )}
            <p className="text-muted mb-0">
              The user will be marked as active and will regain access to the system.
            </p>
            
            {error && (
              <Alert color="danger" className="mt-3">
                <i className="ri-error-warning-line me-2"></i>
                {error}
              </Alert>
            )}
          </div>
          
          <div className="d-flex justify-content-center gap-2 mt-4">
            <Button 
              type="button" 
              color="secondary" 
              onClick={handleReactivateModalClose}
              disabled={reactivateLoading}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              color="success"
              onClick={confirmReactivateUser}
              disabled={reactivateLoading}
            >
              {reactivateLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Reactivating...
                </>
              ) : (
                <>
                  <i className="ri-user-add-line me-2"></i>
                  Reactivate User
                </>
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default TeamManagement;